import React, { useState, useEffect } from "react";
import marketdata from "../../data/Services/marketdata";

/**
 * Debug overlay showing live performance metrics
 * Toggle with: window.toggleDebugOverlay()
 */
const DebugOverlay = () => {
  // Start hidden - no resources consumed until toggled on
  const [visible, setVisible] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [quoteThrottle, setQuoteThrottle] = useState({
    ms: 250,
    label: "Normal",
  });

  // Always expose toggle function (even in production)
  useEffect(() => {
    window.toggleDebugOverlay = () => setVisible((v) => !v);
    return () => {
      delete window.toggleDebugOverlay;
    };
  }, []);

  // Only run metrics interval when visible (zero cost when hidden)
  useEffect(() => {
    if (!visible) return;

    const interval = setInterval(() => {
      if (window.perfMon) {
        const m = window.perfMon.metrics;
        const frameTimes = m.frameTimes || [];
        const avgFrameTime =
          frameTimes.length > 0
            ? frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length
            : 0;
        const fps = avgFrameTime > 0 ? 1000 / avgFrameTime : 0;

        setMetrics({
          fps: fps.toFixed(0),
          accountData: m.accountDataEvents || 0,
          skipped: m.accountDataSkipped || 0,
          reports: m.reportEvents || 0,
          orderStore: m.orderStoreUpdates || 0,
          positionStore: m.positionStoreUpdates || 0,
          indicators: m.indicatorCalculations || 0,
        });
      }

      // Read current quote throttle state
      const throttleMs = marketdata.currentQuoteThrottle || 250;
      let throttleLabel = "Normal";
      if (throttleMs >= 1000) throttleLabel = "Minimal";
      else if (throttleMs >= 500) throttleLabel = "Reduced";
      setQuoteThrottle({ ms: throttleMs, label: throttleLabel });
    }, 500);

    return () => clearInterval(interval);
  }, [visible]);

  if (!visible) return null;

  const style = {
    position: "fixed",
    top: "60px",
    left: "10px",
    background: "rgba(0, 0, 0, 0.85)",
    color: "#0f0",
    padding: "10px 15px",
    borderRadius: "8px",
    fontFamily: "monospace",
    fontSize: "12px",
    zIndex: 99999,
    minWidth: "200px",
    border: "1px solid #0f0",
  };

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    margin: "3px 0",
  };

  const labelStyle = { color: "#aaa" };
  const valueStyle = { color: "#0f0", fontWeight: "bold" };
  const zeroStyle = { color: "#666", fontWeight: "bold" };
  const warnStyle = { color: "#ff0", fontWeight: "bold" };
  const errorStyle = { color: "#f55", fontWeight: "bold" };

  const Row = ({ label, value }) => (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={value > 0 ? valueStyle : zeroStyle}>{value}</span>
    </div>
  );

  // Get throttle color based on state
  const getThrottleStyle = (label) => {
    if (label === "Minimal") return errorStyle;
    if (label === "Reduced") return warnStyle;
    return valueStyle;
  };

  return (
    <div style={style}>
      <div
        style={{
          borderBottom: "1px solid #444",
          paddingBottom: "5px",
          marginBottom: "5px",
          color: "#fff",
        }}
      >
        PERF DEBUG
      </div>
      <Row label="FPS" value={metrics.fps || 0} />
      <div style={rowStyle}>
        <span style={labelStyle}>QuoteThrottle</span>
        <span style={getThrottleStyle(quoteThrottle.label)}>
          {quoteThrottle.label} ({quoteThrottle.ms}ms)
        </span>
      </div>
      <div style={{ borderBottom: "1px solid #333", margin: "5px 0" }} />
      <Row label="AccountData" value={metrics.accountData} />
      <Row label="Skipped" value={metrics.skipped} />
      <Row label="Reports" value={metrics.reports} />
      <div style={{ borderBottom: "1px solid #333", margin: "5px 0" }} />
      <Row label="OrderStore" value={metrics.orderStore} />
      <Row label="PositionStore" value={metrics.positionStore} />
      <Row label="Indicators" value={metrics.indicators} />
      <div style={{ marginTop: "8px", fontSize: "10px", color: "#666" }}>
        window.toggleDebugOverlay()
      </div>
    </div>
  );
};

export default DebugOverlay;
